/**
 * HashtagSets.jsx
 * The saved hashtag groups, as a row of buttons next to the field they fill.
 *
 * Research that lives on its own screen is research nobody uses. This is the
 * thing that makes the Hashtags screen worth having: the set you saved on
 * Tuesday is one click away from the post you are writing on Friday.
 *
 * Appending rather than replacing, and de-duplicating as it goes, because two
 * sets are often meant together and nobody wants to see #launch twice.
 */

import { useEffect, useState } from 'react';
import { api } from '../services/api.js';
import { Hash } from './Icons.jsx';

/**
 * @param {object} props
 * @param {string} props.brandId
 * @param {string} props.value    the field's current contents
 * @param {(next: string) => void} props.onChange
 */
export default function HashtagSets({ brandId, value, onChange }) {
  const [sets, setSets] = useState([]);

  useEffect(() => {
    if (!brandId) {
      setSets([]);
      return;
    }
    let stopped = false;
    api.hashtags
      .sets(brandId)
      .then((d) => !stopped && setSets(d.sets ?? []))
      // A missing set list must never stop a post being written.
      .catch(() => !stopped && setSets([]));
    return () => {
      stopped = true;
    };
  }, [brandId]);

  if (sets.length === 0) return null;

  function apply(tags) {
    const already = new Set(
      (value.match(/#[\p{L}\p{N}_]+/gu) ?? []).map((t) => t.slice(1).toLowerCase()),
    );
    const adding = tags.filter((t) => !already.has(t.toLowerCase())).map((t) => `#${t}`);
    if (adding.length === 0) return;

    onChange([value.trim(), adding.join(' ')].filter(Boolean).join(' '));
  }

  return (
    <div className="besttimes-strip">
      <span className="besttimes-strip__label">
        <Hash size={14} />
        Saved sets
      </span>
      {sets.map((set) => (
        <button
          key={set.id}
          type="button"
          className="chip-btn"
          onClick={() => apply(set.tags)}
          title={set.tags.map((t) => `#${t}`).join(' ')}
        >
          {set.name}
          <span className="chip__lift">{set.tags.length}</span>
        </button>
      ))}
    </div>
  );
}
